import { useEffect, useState } from "react";

/**
 * UI/UX standard rule 2 (debounce search-as-you-type): returns `value` unchanged until it has
 * stopped changing for `delayMs`. The input field itself stays controlled on the raw value by
 * the caller; only the downstream effect keyed on the return of this hook is debounced.
 *
 * An empty string is passed through immediately (no delay) so clearing the field -- via Escape
 * (rule 1) or the in-field X (rule 8) -- resets results right away rather than after a trailing
 * pause, per the standard's "clearing cancels any pending debounced call" note.
 */
export function useDebouncedValue<T>(value: T, delayMs = 350): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (value === "" || value == null) {
      setDebounced(value);
      return;
    }
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
